import React, { useState, useCallback, useEffect, FC } from 'react';
import { useRouter } from '@blocs.router';
import { useTranslation } from '@blocs.i18n';
import Carousel from '@minecraft.carousel';
import appConfig from '@consumer-talent.config/appConfig';
import {
  StyledBenefitsSection,
  SectionBlock,
  SectionHeader,
  ImageWrapper,
  Text,
  StyledModal,
  SectionCenter,
  MainHeader,
} from './styles';

const IntroduceBenefitsCarousel: FC = () => {
  const { t } = useTranslation();
  const {
    searchParams: { isFromOnboarding, isAdditionalAccount },
  } = useRouter<Record<string, string>, { isFromOnboarding?: string; isAdditionalAccount?: string }>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCarouselOpened, setCarouselOpened] = useState(false);
  const [isModalOpened, setModalOpened] = useState(false);
  const isLastCarouselItem = currentSlide === 2;

  useEffect(() => {
    if (isFromOnboarding && !isAdditionalAccount) {
      setModalOpened(true);
    }
  }, [isFromOnboarding, isAdditionalAccount]);

  const handleNextClick = (): void => {
    if (!isCarouselOpened) {
      setCarouselOpened(true);
    } else if (isLastCarouselItem) {
      setModalOpened(false);
    } else {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const updateCurrentSlide = useCallback(
    (index): void => {
      if (currentSlide !== index) {
        setCurrentSlide(index);
      }
    },
    [currentSlide]
  );

  if (!isModalOpened) return null;

  return (
    <StyledModal
      modalSize="medium"
      isOpen={isModalOpened}
      title=""
      submitLabel={isLastCarouselItem ? t('common:button.done') : t('common:button.next')}
      handleToggleClick={(): void => {
        // close disabled
        return null;
      }}
      handleSubmitClick={handleNextClick}
    >
      {!isCarouselOpened ? (
        <SectionCenter>
          <img src={`${appConfig.cdnRoot}/images/logos/cn-icon-only.svg`} alt={t('talent:introduction.mainImgAlt')} />
          <MainHeader>{t('talent:introduction.mainHeader')}</MainHeader>
          <Text>{t('talent:introduction.mainText')}</Text>
        </SectionCenter>
      ) : (
        <Carousel currentSlide={currentSlide} updateCurrentSlide={updateCurrentSlide}>
          <StyledBenefitsSection>
            <SectionBlock>
              <SectionHeader>{t('talent:introduction.sectionOneHeader')}</SectionHeader>
              <Text>{t('talent:introduction.sectionOneText')}</Text>
            </SectionBlock>
            <ImageWrapper>
              <img
                src={`${appConfig.cdnRoot}/images/static/profile-dropdown.png`}
                srcSet={`${appConfig.cdnRoot}/images/static/profile-dropdown%25402x.png 2x`}
                alt={t('talent:introduction.sectionOneImgAlt')}
              />
            </ImageWrapper>
          </StyledBenefitsSection>
          <StyledBenefitsSection>
            <SectionBlock>
              <SectionHeader>{t('talent:introduction.sectionTwoHeader')}</SectionHeader>
              <Text>{t('talent:introduction.sectionTwoText')}</Text>
            </SectionBlock>
            <ImageWrapper>
              <img
                src={`${appConfig.cdnRoot}/images/static/interface.png`}
                srcSet={`${appConfig.cdnRoot}/images/static/interface%25402x.png 2x`}
                alt={t('talent:introduction.sectionTwoImgAlt')}
              />
            </ImageWrapper>
          </StyledBenefitsSection>
          <StyledBenefitsSection>
            <SectionBlock>
              <SectionHeader>{t('talent:introduction.sectionThreeHeader')}</SectionHeader>
              <Text>{t('talent:introduction.sectionThreeText')}</Text>
            </SectionBlock>
            <ImageWrapper>
              <img
                src={`${appConfig.cdnRoot}/images/icons/b21/self-tape-upload.svg`}
                alt={t('talent:introduction.sectionThreeImgAlt')}
              />
            </ImageWrapper>
          </StyledBenefitsSection>
        </Carousel>
      )}
    </StyledModal>
  );
};

export default IntroduceBenefitsCarousel;
